import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { TranslateModule } from '@ngx-translate/core';

export interface DropdownOption {
  label: string;
  value: any;
  disabled?: boolean;
  children?: DropdownOption[];
  expanded?: boolean;
  parent?: string;
}

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, TranslateModule],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true
    }
  ]
})
export class DropdownComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() options: DropdownOption[] = [];
  @Input() placeholder?: string = 'Select an option';
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() filter: boolean = false;
  @Input() filterBy: string = 'label';
  @Input() filterPlaceholder: string = 'Search...';
  @Input() showClear: boolean = true;
  @Input() optionLabel?: string = 'label';
  @Input() optionValue?: string = 'value';
  @Input() optionDisabled: string = 'disabled';
  @Input() group: boolean = false;
  @Input() groupLabel: string = 'label';
  @Input() groupChildren: string = 'items';
  @Input() appendTo: string = 'body';
  @Input() autofocus: boolean = false;
  @Input() editable: boolean = false;
  @Input() maxlength: number | undefined = undefined;
  @Input() tree: boolean = false;
  @Input() loading: boolean = false;

  @Output() onChange = new EventEmitter<any>();
  @Output() onFilter = new EventEmitter<any>();
  @Output() onFocus = new EventEmitter<Event>();
  @Output() onBlur = new EventEmitter<Event>();
  @Output() onShow = new EventEmitter<void>();
  @Output() onHide = new EventEmitter<void>();
  @Output() onClear = new EventEmitter<void>();

  value: any = null;
  processedOptions: DropdownOption[] = [];

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleChange(value: any): void {
    this.value = value;
    this.onChangeCallback(value);
    this.onChange.emit(value);
  }

  handleBlur(event: Event): void {
    this.onTouchedCallback();
    this.onBlur.emit(event);
  }

  handleFocus(event: Event): void {
    this.onFocus.emit(event);
  }

  handleFilter(event: any): void {
    this.onFilter.emit(event);
  }

  handleShow(): void {
    this.onShow.emit();
  }

  handleHide(): void {
    this.onHide.emit();
  }

  handleClear(): void {
    this.value = null;
    this.onChangeCallback(null);
    this.onChange.emit(null);
    this.onClear.emit();
  }

  ngOnInit(): void {
    if (this.tree) {
      this.processTreeOptions();
    }
  }

  ngOnChanges(): void {
    if (this.tree) {
      this.processTreeOptions();
    }
  }

  processTreeOptions(): void {
    if (!this.tree) {
      this.processedOptions = this.options || [];
      return;
    }

    const flattened: DropdownOption[] = [];
    this.options.forEach(parent => {
      if (parent.children) {
        parent.children.forEach(child => {
          flattened.push({
            ...child,
            label: `${parent.label} - ${child.label}`,
            value: child.value
          });
        });
      }
    });

    this.processedOptions = flattened;
  }

  getSelectedLabel(): string {
    if (!this.value || !this.tree) {
      return this.placeholder || '';
    }

    for (const parent of this.options) {
      if (parent.children) {
        const child = parent.children.find(c => c.value === this.value);
        if (child) {
          return `${parent.label} - ${child.label}`;
        }
      }
    }

    return this.placeholder || '';
  }

  toggleExpand(event: Event, option: DropdownOption): void {
    event.stopPropagation();
    event.preventDefault();
    option.expanded = !option.expanded;
  }

  selectChild(event: Event, child: DropdownOption, parent: DropdownOption): void {
    event.stopPropagation();

    this.handleChange(child.value);

    this.handleHide();
  }

  isChildSelected(child: DropdownOption): boolean {
    return this.value === child.value;
  }
}
